import { useMemo, useEffect, useState } from 'react';
import { useAutocomplete } from './useAutocomplete';
import { getContentAutocomplete } from '../services/apis/ContentsApi';

/**
 * 콘텐츠 자동완성 훅
 * @param {Object} props
 * @param {string} props.searchText 검색어
 * @param {number} props.minLength 최소 검색 길이
 * @param {number} props.limit 결과 제한
 */
export function useContentAutocomplete({
	searchText,
	minLength = 2,
	limit = 10,
}) {
	const [data, setData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	// 검색 조건 확인
	const shouldFetch = useMemo(() => {
		return searchText && searchText.length >= minLength;
	}, [searchText, minLength]);

	// 한글 초성 검색을 위한 검색어 확장
	const expandedSearchText = useMemo(() => {
		if (!searchText || searchText.length < 2) return searchText;

		// 초성만으로 이루어진 검색어인지 확인
		const isChosungOnly = /^[ㄱ-ㅎ]+$/.test(searchText);

		if (isChosungOnly) {
			// 실제로는 너무 많은 조합이 생성될 수 있으므로 제한적으로 사용
			return searchText; // 일단 원본 그대로 전송하고 서버에서 처리
		}

		return searchText;
	}, [searchText]);

	// API 호출
	useEffect(() => {
		if (!shouldFetch) {
			setData({ suggestions: [] });
			setIsLoading(false);
			setError(null);
			return;
		}

		const fetchAutocomplete = async () => {
			try {
				setIsLoading(true);
				setError(null);
				const result = await getContentAutocomplete(expandedSearchText, limit);

				setData(result);
			} catch (err) {
				console.error('❌ 자동완성 에러:', err);
				setError(err);
				setData({ suggestions: [] });
			} finally {
				setIsLoading(false);
			}
		};

		// 디바운싱을 위한 타이머 (제거 - SearchComponent에서 이미 디바운싱 처리됨)
		fetchAutocomplete();
	}, [shouldFetch, expandedSearchText, limit]);

	// 자동완성 훅 설정
	const autocomplete = useAutocomplete({
		items: data?.suggestions || [],
		searchKeys: ['title', 'matchedText'],
		maxResults: limit,
		minSearchLength: 0, // API에서 이미 필터링되므로 0으로 설정
		filterFn: () => true, // API에서 이미 필터링됨
		mapFn: (suggestion) => ({
			id: suggestion.uuid,
			label: suggestion.title, // 태그는 태그명, 제목은 콘텐츠 제목
			value: suggestion.title, // 검색할 때 사용할 값 (태그명 또는 콘텐츠 제목)
			type: suggestion.type,
			data: suggestion,
		}),
	});

	// 외부 검색어와 내부 상태 동기화
	useEffect(() => {
		if (autocomplete.searchText !== searchText && shouldFetch) {
			autocomplete.setSearchText(searchText);
		}
	}, [searchText, shouldFetch, autocomplete]);

	return {
		autocomplete,
		isLoading,
		error,
		suggestions: data?.suggestions || [],
	};
}
